import { useEffect, useRef, useState } from "react";
import { ApiError, isAbortError, withReadSignal, type TodayPage } from "./client.ts";
import { usePortal, type Load } from "./hooks.ts";
import type { Queue } from "./schemas.ts";

type Collection = "assignments" | "reviews";

/** Independent cursors, one lifetime. A changed cursor discards both collections before restarting. */
export function useTodayQueue(analyst: string, filters: TodayPage, enabled = true) {
  const { api, expire } = usePortal();
  const key = JSON.stringify([analyst, filters, enabled]);
  const [state, setState] = useState<{ key: string; value: Load<Queue> }>({ key: "", value: { state: "loading" } });
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState<Collection | null>(null);
  const [moreError, setMoreError] = useState<Error | null>(null);
  const generation = useRef(0);
  const active = useRef<AbortController | null>(null);
  const current = useRef<Queue | null>(null);
  const locked = useRef(false);
  const preserve = useRef(false);
  const windowSize = useRef({ key, assignments: 0, open_review_items: 0 });
  const value: Load<Queue> = state.key === key ? state.value : { state: "loading" };
  current.current = value.state === "ready" ? value.data : null;
  if (windowSize.current.key !== key) windowSize.current = { key, assignments: 0, open_review_items: 0 };
  if (current.current) {
    windowSize.current.assignments = Math.max(windowSize.current.assignments, current.current.assignments.length);
    windowSize.current.open_review_items = Math.max(windowSize.current.open_review_items, current.current.open_review_items.length);
  }

  // A next page is the first page's view: its cursor is bound to the Outlook state that page was worded in, which is the
  // server's own read when none was sent (AIC-992), so it goes back with the cursor even if the connection changed since.
  const nextPage = (first: Queue, cursor: string): TodayPage => ({ ...filters, outlook: filters.outlook ?? first.filters?.outlook, limit: 50, cursor });

  // Ordinary refresh keeps the selected evidence/notes mounted. Cursor recovery must drop stale rows.
  function reload(keep = true) {
    preserve.current = keep;
    generation.current++;
    active.current?.abort();
    if (!keep) {
      windowSize.current = { key, assignments: 0, open_review_items: 0 };
      current.current = null;
      setState({ key, value: { state: "loading" } });
    }
    setTick(t => t + 1);
  }

  useEffect(() => {
    const gen = ++generation.current;
    const controller = new AbortController();
    active.current = controller;
    locked.current = false;
    setBusy(null);
    setMoreError(null);
    const keep = preserve.current;
    const prior = keep ? current.current : null;
    preserve.current = false;
    setState(old => keep && old.key === key && old.value.state === "ready" ? old : { key, value: { state: "loading" } });
    if (enabled) {
      const read = withReadSignal(api, controller.signal);
      async function refresh() {
        let data = await read.queue(analyst || undefined, { ...filters, limit: 50 });
        // Refresh the entire loaded window using NEW cursors after a mutation, not stale cursors.
        // Publish atomically so the selected evidence and appended rows remain mounted.
        for (const collection of ["assignments", "reviews"] as const) {
          const field = collection === "assignments" ? "assignments" : "open_review_items";
          const cursorKey = collection === "assignments" ? "assignment_cursor" : "review_cursor";
          const target = prior ? windowSize.current[field] : 0;
          while (data[field].length < target && data[cursorKey]) {
            const next = await read.queue(analyst || undefined, nextPage(data, data[cursorKey]!));
            if (gen !== generation.current) return data;
            if (data.revision && next.revision && (data.revision.assignments !== next.revision.assignments || data.revision.open_review_items !== next.revision.open_review_items)) {
              throw new ApiError(409, "queue_changed");
            }
            data = { ...data, [field]: [...data[field], ...next[field]], [cursorKey]: next[cursorKey] };
          }
        }
        return data;
      }
      void refresh().then(
        data => { if (gen === generation.current) setState({ key, value: { state: "ready", data } }); },
        error => {
          if (gen !== generation.current || isAbortError(error)) return;
          if (prior && error instanceof ApiError && ((error.status === 409 && error.code === "queue_changed") || (error.status === 400 && error.code === "bad_cursor"))) {
            reload(false);
            return;
          }
          if (error instanceof ApiError && error.status === 401) expire();
          setState({ key, value: { state: "error", error: error instanceof Error ? error : new Error(String(error)) } });
        },
      );
    }
    return () => { generation.current++; controller.abort(); };
  }, [api, expire, key, tick]);

  async function more(collection: Collection) {
    const prior = current.current;
    const cursor = collection === "assignments" ? prior?.assignment_cursor : prior?.review_cursor;
    if (!prior || !cursor || locked.current) return;
    locked.current = true;
    setBusy(collection);
    setMoreError(null);
    const gen = generation.current;
    try {
      const next = await withReadSignal(api, active.current!.signal).queue(analyst || undefined, nextPage(prior, cursor));
      if (gen !== generation.current) return;
      if (prior.revision && next.revision && (prior.revision.assignments !== next.revision.assignments || prior.revision.open_review_items !== next.revision.open_review_items)) {
        reload(false);
        return;
      }
      const data: Queue = {
        ...prior, ...next,
        assignments: collection === "assignments" ? [...prior.assignments, ...next.assignments] : prior.assignments,
        open_review_items: collection === "reviews" ? [...prior.open_review_items, ...next.open_review_items] : prior.open_review_items,
        assignment_cursor: collection === "assignments" ? next.assignment_cursor : prior.assignment_cursor,
        review_cursor: collection === "reviews" ? next.review_cursor : prior.review_cursor,
        totals: next.totals ? { ...next.totals, by_company: prior.totals?.by_company } : prior.totals,
      };
      setState({ key, value: { state: "ready", data } });
    } catch (error) {
      if (gen !== generation.current || isAbortError(error)) return;
      if (error instanceof ApiError && ((error.status === 409 && error.code === "queue_changed") || (error.status === 400 && error.code === "bad_cursor"))) {
        reload(false);
        return;
      }
      if (error instanceof ApiError && error.status === 401) expire();
      setMoreError(error instanceof Error ? error : new Error(String(error)));
    } finally {
      if (gen === generation.current) { locked.current = false; setBusy(null); }
    }
  }
  return { ...value, reload, more, busy, moreError };
}
